import { useState, useRef, useCallback, useEffect } from "react";
import { monitoringApi } from "../api/monitoringApi";

function pivotFiringCustomers(byCustomer) {
  const map = {};
  (byCustomer || []).forEach(({ customer_name, status, count }) => {
    if (!customer_name) return;
    if (!map[customer_name]) map[customer_name] = { name: customer_name, firing: 0, resolved: 0 };
    if (status === "firing")   map[customer_name].firing   = Number(count);
    if (status === "resolved") map[customer_name].resolved = Number(count);
  });
  return Object.values(map)
    .filter(t => t.firing > 0)
    .sort((a, b) => b.firing - a.firing || a.name.localeCompare(b.name));
}

export function useAlertWidget(refreshInterval = 0) {
  const [pipWindow,        setPipWindow]        = useState(null);
  const [firingCustomers,  setFiringCustomers]  = useState([]);
  const [lastUpdated,      setLastUpdated]      = useState(null);

  const intervalRef    = useRef(null);
  const seenNamesRef   = useRef(new Set());
  const initialDoneRef = useRef(false);

  const effectiveInterval = refreshInterval > 0 ? refreshInterval : 30000;

  const load = useCallback(async () => {
    try {
      const statsData   = await monitoringApi.alertStats({});
      const byCustomer  = Array.isArray(statsData?.by_customer) ? statsData.by_customer : [];
      const tiles       = pivotFiringCustomers(byCustomer);

      // Play audio when a customer appears as firing for the first time
      const newFiring = tiles.filter(t => !seenNamesRef.current.has(t.name));
      if (initialDoneRef.current && newFiring.length > 0) {
        new Audio("/alert-ping.wav").play().catch(() => {});
      }
      initialDoneRef.current = true;
      seenNamesRef.current   = new Set(tiles.map(t => t.name));

      setFiringCustomers(tiles);
      setLastUpdated(new Date());
    } catch {
      // keep prior state on error
    }
  }, []);

  // Start/stop fetch loop with the PiP window lifecycle
  useEffect(() => {
    if (!pipWindow) { clearInterval(intervalRef.current); return; }
    load();
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(load, effectiveInterval);
    return () => clearInterval(intervalRef.current);
  }, [pipWindow, effectiveInterval, load]);

  const close = useCallback(() => {
    if (pipWindow) pipWindow.close();
  }, [pipWindow]);

  const launch = useCallback(async (showToast) => {
    if (!window.documentPictureInPicture) {
      showToast?.("Document Picture-in-Picture requires Chrome 116+. Not supported in this browser.", "warn");
      return;
    }
    if (pipWindow) { pipWindow.focus(); return; }

    // Unlock audio context on the user gesture
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      await ctx.resume();
      ctx.close();
    } catch { /* not fatal */ }

    try {
      const pip = await window.documentPictureInPicture.requestWindow({
        width: 350, height: 260,
      });

      const reset = pip.document.createElement("style");
      reset.textContent = "*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }";
      pip.document.head.appendChild(reset);

      document.head.querySelectorAll("style, link[rel='stylesheet']").forEach(el => {
        pip.document.head.appendChild(el.cloneNode(true));
      });

      pip.document.body.style.cssText = "overflow:hidden;background:#F4F1EA;";

      pip.addEventListener("pagehide", () => {
        clearInterval(intervalRef.current);
        initialDoneRef.current = false;
        seenNamesRef.current   = new Set();
        setPipWindow(null);
      });

      setPipWindow(pip);
    } catch (err) {
      showToast?.(`Could not open widget: ${err.message}`, "warn");
    }
  }, [pipWindow]);

  return { isOpen: !!pipWindow, pipWindow, launch, close, firingCustomers, lastUpdated };
}
